import React, { useState, useEffect, useRef } from 'react';
import { Gitgraph, templateExtend, TemplateName } from '@gitgraph/react';
import './gitbody.scss';
import { Overlay, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CommitOptions } from '@gitgraph/react/lib/types';
import { useLanguage } from '../../contexts/languageContext';
import themecolor from '../theme';
import { useTheme } from '../../contexts/themeContext';
import { useWindowSize } from '../../contexts/windowSizeContext';
import { confetti } from 'tsparticles-confetti';
import ResourceViewerModal from '../resource-viewer-modal/ResourceViewerModal';
import newspaper from '../../assets/newspaper.jpeg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChain } from '@fortawesome/free-solid-svg-icons';
import { trackEvent } from '../../lib/analytics';
// Custom components for each commit

const Gitbody: React.FC = () => {
  const { language, t } = useLanguage();
  const { width } = useWindowSize();
  const messagewidth: number =
    ((width - 500) ) < 500 ? 500 : ((width - 500 - 25) ) ;
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const lastHashcommit: string = '44b79548-ea22-4fe1-86f1-98e6b52db5fe';
  // removed old toggleExpand stub
  const { theme } = useTheme();
  const [show] = useState(false);
  const [target] = useState<(EventTarget & SVGGElement) | null>(null);
  const [tooltipContent] = useState('');
  // timestamp (ms) of last time we triggered the mailto; used to rate-limit email sends
  const lastMailSentRef = useRef<number>(0);
  const primarycolor = theme === 'casual' ? themecolor.casualprimary : themecolor.businessprimary;
  const secondarycolor =
    theme === 'casual' ? themecolor.casualsecondary : themecolor.businesssecondary;

  const changekey: string = `${language}-${messagewidth}`;

  // Canvas-based helpers to measure and wrap SVG <text> content to a max width
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const measureTextWidth = (text: string, font = '14px system-ui'): number => {
  canvasRef.current ??= document.createElement('canvas');
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return text.length * 8; // rough fallback
    ctx.font = font;
    return ctx.measureText(text).width;
  };

  const shrinkToFit = (text: string, maxWidth: number, font = '14px system-ui'): string => {
    if (measureTextWidth(text, font) <= maxWidth) return text;
    const ellipsis = '…';
    let low = 0;
    let high = text.length;
    let best = '';
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = text.slice(0, mid).trim() + ellipsis;
      if (measureTextWidth(candidate, font) <= maxWidth) {
        best = candidate;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return best || ellipsis;
  };

  const wrapSvgText = (
    text: string,
    maxWidth: number,
    font = '14px system-ui'
  ): string[] => {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';

    for (const word of words) {
      const tentative = current ? `${current} ${word}` : word;
      if (measureTextWidth(tentative, font) <= maxWidth) {
        current = tentative;
      } else {
        if (current) lines.push(current);
        else lines.push(shrinkToFit(word, maxWidth, font));
        current = '';
        if (lines.length >= 2) break; // we'll consolidate extra content into line 2
      }
    }
    if (lines.length < 2 && current) lines.push(current);

    if (lines.length > 2) {
      // consolidate into 2 lines, shrinking the second if needed
      const second = lines.slice(1).join(' ');
      lines.length = 1;
      lines[1] = shrinkToFit(second, maxWidth, font);
    }
    if (lines.length === 2 && measureTextWidth(lines[1], font) > maxWidth) {
      lines[1] = shrinkToFit(lines[1], maxWidth, font);
    }
    return lines.slice(0, 2);
  };

  let options = {
    branchLabelOnEveryCommit: false,
    template: templateExtend(TemplateName.Metro, {
      colors: [primarycolor, secondarycolor, '#ffc107', '#fd7e14', '#6f42c1', '#4f4f4f', '#fd7e14'],
      branch: {
        lineWidth: 6,
        spacing: 30,
        label: {
          //  display: false
        },
      },
      commit: {
        
        message: {
          displayAuthor: false,
          displayHash: false,
        },
        dot: {
          size: 12,
          strokeColor: 'white',
          strokeWidth: 2,
        },
       
        spacing: 50,
      },
      arrow: {
        size: 0,
        color: 'orange',
        offset: -1.5,
      },
      tag: {
           font: '10pt',
           
      }
    }),
  };

  const programmingloveMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          &#128150;&#128187; {commit.subject}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('programminglovemessage')}</p>
        </foreignObject>
      </g>
    );
  };
  const cryptographyLoveMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          &#128373; {t('cryptographylove')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('cryptographyloveMessage')}</p>
        </foreignObject>
      </g>
    );
  };
  const universitypartnershipMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('universityPartnership')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('universitypartnershipMessage')}</p>
        </foreignObject>
      </g>
    );
  };
  const otherWorkMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('startotherwork')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('otherworkmessage')}</p>
        </foreignObject>
      </g>
    );
  };
  const universityMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('universityTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('universityMessage')}</p>
        </foreignObject>
      </g>
    );
  };

  const universityThesisMessage: CommitOptions['renderMessage'] = (commit: any) => {
    const title = t('universityThesisTitle');
    const titleLines = wrapSvgText(title, messagewidth-120);
    const bodyYOffset = titleLines.length > 1 ? 26 : 22; // push body down if title wraps
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          <tspan x="0" dy="0">
            {titleLines[0]}
          </tspan>
          {titleLines[1] && (
            <tspan x="0" dy="1.2em">
              {titleLines[1]}
            </tspan>
          )}
        </text>
        <foreignObject width={messagewidth} y={bodyYOffset}>
          <p>{t('universityThesisMessage')}</p>
        </foreignObject>
      </g>
    );
  };

  const newspaperMentionMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('newspaperMentionTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>
            {t('newspaperMentionMessage')}{' '}
            <ResourceViewerModal buttonClassName="btn btn-link" url={newspaper} title={''}>
              {t('readme')}
            </ResourceViewerModal>
          </p>
        </foreignObject>
      </g>
    );
  };
  const oltrebaseMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          
          {t('oltrebaseTitle')}
        </text>
        <foreignObject width={messagewidth} y="-12">
          <p>
            <div className="position-relative top--12">
              <div className="position-absolute top-50 end-0 translate-middle-y">
                <strong>{t('oltrebaseCompany')}</strong>{' '}
                <a
                  href={t('oltrebaseLink')}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open Oltrebase website"
                  title="Open Oltrebase website"
                  className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
                >
                  <FontAwesomeIcon icon={faChain} />
                </a>
              </div>
            </div>
            <div className="mt-24" dangerouslySetInnerHTML={{ __html: t('oltrebaseMessage') }}></div>
          </p>
        </foreignObject>
      </g>
    );
  };
  const oltrebaseFocusProjects: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('oltrebaseFocusProjects')}
        </text>

        <foreignObject width={messagewidth} y="22">
          <p dangerouslySetInnerHTML={{ __html: t('oltrebaseFocusProjectsMessage') }}></p>
        </foreignObject>
      </g>
    );
  };
  const atgMessage1: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('atgTitle')}
        </text>
        <foreignObject width={messagewidth} y="-12">
          <p>
            <div className="position-relative top--12">
              <div className="position-absolute top-50 end-0 translate-middle-y">
                <strong>{t('atgCompany')}</strong>{' '}
                <a
                  href={t('atgLink')}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open ATG website"
                  title="Open ATG website"
                  className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
                >
                  <FontAwesomeIcon icon={faChain} />
                </a>
              </div>
            </div>
            <div className="mt-24" dangerouslySetInnerHTML={{ __html: t('atgMessage') }}></div>
          </p>
        </foreignObject>
      </g>
    );
  };
  const atgFocusProject: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('atgTitleFocus')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p dangerouslySetInnerHTML={{ __html: t('atgFocusProjectMessage') }}></p>
        </foreignObject>
      </g>
    );
  };
  const zegnaMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('zegnaTitle')}
        </text>
        <foreignObject width={messagewidth} y="-12">
          <p>
            <div className="position-relative top--12">
              <div className="position-absolute top-50 end-0 translate-middle-y">
                <strong>{t('zegnaCompany')}</strong>{' '}
                <a
                  href={t('zegnaLink')}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open Zegna website"
                  title="Open Zegna website"
                  className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
                >
                  <FontAwesomeIcon icon={faChain} />
                </a>
              </div>
            </div>
            <div className="mt-24" dangerouslySetInnerHTML={{ __html: t('zegnaMessage') }}></div>
          </p>
        </foreignObject>
      </g>
    );
  };
  const zegnaFocusProject: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('zegnaTitleFocus')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p dangerouslySetInnerHTML={{ __html: t('zegnaFocusProjectMessage') }}></p>
        </foreignObject>
      </g>
    );
  };
  const swatchMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('swatchTitle')}
        </text>
        <foreignObject width={messagewidth} y="-12">
          <p>
            <div className="position-relative top--12">
              <div className="position-absolute top-50 end-0 translate-middle-y">
                <strong>{t('swatchCompany')}</strong>{' '}
                <a
                  href={t('swatchgroupLink')}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open Swatch Group website"
                  title="Open Swatch Group website"
                  className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
                >
                  <FontAwesomeIcon icon={faChain} />
                </a>
              </div>
            </div>
            <div className="mt-24" dangerouslySetInnerHTML={{ __html: t('swatchMessage') }}></div>
          </p>
        </foreignObject>
      </g>
    );
  };
  const swatchFocusProject: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('swatchTitleFocus')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p dangerouslySetInnerHTML={{ __html: t('swatchFocusProjectMessage') }}></p>
        </foreignObject>
      </g>
    );
  };
  const openlogsMessage: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('openlogsTitle')}
        </text>
        <foreignObject width={messagewidth} y="-12">
          <p>
            <div className="position-relative top--12">
              <div className="position-absolute top-50 end-0 translate-middle-y">
                <strong>{t('openlogsCompany')}</strong>{' '}
                <a
                  href={t('openlogsLink')}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open Openlogs website"
                  title="Open Openlogs website"
                  className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
                >
                  <FontAwesomeIcon icon={faChain} />
                </a>
              </div>
            </div>
            <div className="mt-24" dangerouslySetInnerHTML={{ __html: t('openlogsMessage') }}></div>
          </p>
        </foreignObject>
      </g>
    );
  };
  const openlogsFocusProject: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('openlogsTitleFocus')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p dangerouslySetInnerHTML={{ __html: t('openlogsFocusProjectMessage') }}></p>
        </foreignObject>
      </g>
    );
  };
  const microsoftSqlServer: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('sqlCertificationTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p dangerouslySetInnerHTML={{ __html: t('sqlCertificationMessage') }}></p>
        </foreignObject>
      </g>
    );
  };
  const agilecertification: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('agileCertificationTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('agileCertificationMessage')}</p>
        </foreignObject>
      </g>
    );
  };
  const microsoftcsharp: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('microsoftcsharpTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('microsoftcsharpMessage')}</p>
        </foreignObject>
      </g>
    );
  };
  const azureadministrator: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('azureadministratorTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('azureadministratorMessage')}</p>
        </foreignObject>
      </g>
    );
    };
     const pqccertification: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('PqcCertificationTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('PqcCertificationMessage')}</p>
        </foreignObject>
      </g>
    );
    };
     const ibmcyberdefencecertification: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('cyberdefenceCertificationTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('cyberdefenceCertificationMessage')}</p>
        </foreignObject>
      </g>
    );
  };
  const taisecertification: CommitOptions['renderMessage'] = (commit: any) => {
    return (
      <g transform={`translate(0, ${commit.style.dot.size})`}>
        <text fill={commit.style.dot.color} alignmentBaseline="central">
          {t('taiseCertificationTitle')}
        </text>
        <foreignObject width={messagewidth} y="22">
          <p>{t('taiseCertificationMessage')}</p>
        </foreignObject>
      </g>
    );
  };

   
  const renderTooltip = (props: any) => (
    <Tooltip id="overlay-branchme" {...props}>
      {t('BranchMe')}
    </Tooltip>
  );

  const confettiExplode = (event: React.MouseEvent<SVGElement>) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();

    // Calcola la posizione relativa del centro dell'SVG
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    confetti({
      particleCount: 200,
      spread: 150,
      origin: { x, y },
      colors: [primarycolor, secondarycolor, '#ffc107', '#fd7e14', '#6f42c1'],
      shapes: ['square', 'circle'],
      scalar: 1.2,
      zIndex: 1500,
      gravity: 0.7,
      ticks: 300,
    });
    // Rate-limit sending the mailto: only once every 20 seconds
    const now = Date.now();
    const MIN_INTERVAL_MS = 20_000; // 20 seconds
    const canSendEmail = now - lastMailSentRef.current >= MIN_INTERVAL_MS;

    if (canSendEmail) {
      // record the send time immediately so subsequent clicks within the interval won't schedule another mail
      lastMailSentRef.current = now;
      try { trackEvent('contact_me_click', { source: 'confetti' }); } catch {}
      setTimeout(() => {
        const recipient = t('contactEmailAddress');

        const subject = 'Can I branch You?';
        const body =
          'Hi Luca,\n\n' +
          'I am extremely impressed with your resume. I think someone like you is a perfect fit for our company.';

        // Usiamo encodeURIComponent per formattare correttamente i caratteri speciali
        const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

        // Imposta window.location.href per attivare il client di posta
        window.location.href = mailtoLink;
      }, 1500);
    }
  };

  const lastcommitdot: any = (commit: any) => {
    return (
      <OverlayTrigger placement="right" delay={{ show: 250, hide: 400 }} overlay={renderTooltip}>
        <svg
          className="concentric-container"
          onClick={confettiExplode}
          id={commit.hash}
          width="40"
          height="40"
          viewBox="0 0 100 100"
        >
          <g>
            <circle id="outer-circle" cx="40" cy="40" r="35" />
            <circle id="inner-circle" cx="40" cy="40" r="10" />
          </g>
        </svg>
      </OverlayTrigger>
    );
  };

  // formatDate removed (unused)

  return (
    <div
      className="container-fluid no-print mb-2 me-1"
     
    >
      <Overlay target={target} show={show} placement="right">
        {(props) => (
          <Tooltip id="overlay-example" {...props}>
            {tooltipContent}s
          </Tooltip>
        )}
      </Overlay>
      <Gitgraph options={options} key={changekey}>
        {(gitgraph) => {
          let mebranch = gitgraph.branch('Me');
          mebranch.commit({ subject: t('born'), author: t('parents'), tag: t('borndate') });
          mebranch.commit({ subject: t('startstudy'), author: 'Me', tag: t('startstudidate') });
          let edubranch = mebranch.branch('High School');
          edubranch.commit({
            subject: t('programmingLove'),
            author: 'Me',
            tag: t('programminglovedate'),
            renderMessage: programmingloveMessage,
          });
          edubranch.commit({ renderMessage: cryptographyLoveMessage, author: 'Me' });
          mebranch.commit({ subject: t('im18'), tag: t('im18date'), author: 'The Time' });
          let otherwork = mebranch.branch('Secondary Jobs');
          otherwork.commit({ renderMessage: otherWorkMessage, author: 'Me' });
          edubranch.commit({ renderMessage: universitypartnershipMessage, author: 'Me' });
          mebranch.merge(edubranch, t('completehighschool'));
          // timeline nodes
          let edubranch2 = mebranch.branch('University');
          edubranch2.commit({ renderMessage: universityMessage, author: 'Me' });
          otherwork.commit({
            subject: t('juniorSysadmin'),
            author: 'Me',
            tag: t('junionrSysadminDate'),
          });
          edubranch2
            .commit({ renderMessage: universityThesisMessage, author: 'Me' })
            .tag(t('thesisdate'))
            .tag(t('cryptography'));
          edubranch2
            .commit({ renderMessage: newspaperMentionMessage, author: 'Me' })
            .tag(t('newspaperdate'));
          mebranch.merge(edubranch2, t('universityComplete'));

          let mainJob = mebranch.branch('Main Jobs');
          mainJob.commit({
            renderMessage: oltrebaseMessage,
            tag: t('oltrebaseDate'),
            author: 'Me',
          });
          mainJob.commit({ renderMessage: oltrebaseFocusProjects,   author: 'Me' }).tag('Focus ');
          mebranch.merge(mainJob, t('endingOltrebaseJobExperience'));
          mainJob.commit({ renderMessage: atgMessage1, tag: t('atgstartDate'), author: 'Me' });
          mainJob.commit({ renderMessage: atgFocusProject,   author: 'Me' }).tag('Focus');
          mebranch.merge(mainJob, t('endingAtgJobExperience'));
          mebranch.merge(otherwork, t('endSecondaryJobs'));
          mainJob.commit({ renderMessage: zegnaMessage, tag: t('zegnastartDate'), author: 'Me' });
          let certification = mebranch.branch(t('Certifications'));
          certification
            .commit({ renderMessage: microsoftSqlServer, author: 'Me' })
            .tag(t('sqlCertificationDate'));

          mainJob.commit({ renderMessage: zegnaFocusProject,   author: 'Me' }).tag('Focus  ');
          mebranch.merge(mainJob, t('endingZegnaJobExperience'));
          mainJob.commit({ renderMessage: swatchMessage, author: 'Me', tag: t('swatchstartDate') });
          certification
            .commit({ renderMessage: agilecertification, author: 'Me' })
            .tag(t('agileCertificationDate'));
          certification
            .commit({ renderMessage: microsoftcsharp, author: 'Me' })
            .tag(t('microsoftcsharpCertificationDate'));
          certification
            .commit({ renderMessage: azureadministrator, author: 'Me' })
            .tag(t('azureAdminCertificationDate'));
         
          certification
          .commit({ renderMessage: pqccertification, author: 'Me' })
          .tag(t('PqcCertificationDate'));
          certification
          .commit({ renderMessage: ibmcyberdefencecertification, author: 'Me' })
          .tag(t('cyberdefenceCertificationDate'));
        
          mainJob.commit({ renderMessage: swatchFocusProject,   author: 'Me' }).tag('Focus   ');
          mebranch.merge(mainJob, t('endingSwatchJobExperience'));
          mainJob.commit({ renderMessage: openlogsMessage, author: 'Me', tag: t('openlogsStartDate') });
          mainJob.commit({ renderMessage: openlogsFocusProject,   author: 'Me' }).tag('Focus    ');
           certification
            .commit({ renderMessage: taisecertification, author: 'Me' })
            .tag(t('taiseCertificationDate'));
          // mebranch.merge(certification, t('CertificationsComplete'));
          mebranch.commit({
            renderMessage: (commit: any) => {
              const locale = language === 'en' ? 'en-US' : 'it-IT';
              return (
                <g transform={`translate(0, ${commit.style.dot.size})`}>
                  <text fill={commit.style.dot.color} alignmentBaseline="central">
                    {currentTime.toLocaleDateString(locale)}{' '}
                    {currentTime.toLocaleTimeString(locale)}
                  </text>
                </g>
              );
            },

            author: 'Me',
            renderDot: lastcommitdot,
            hash: lastHashcommit,
            onMouseOver: (commit: any) => {
              console.log('commit', commit);
              console.log('lastHashcommit', lastHashcommit);
             
            },
            onMouseOut: () => {
              /* no-op */
            },
          });
        }}
      </Gitgraph>
    </div>
  );
};

export default Gitbody;
